(() => {
  const selectors = {
    root: "[data-abc-shipping-estimator]",
    country: "[data-shipping-country]",
    status: "[data-shipping-status]",
    rates: "[data-shipping-rates]",
    productRoot: "[data-abc-product-shipping-country]",
    productCountry: "[data-product-shipping-country]",
    productStatus: "[data-product-shipping-status]",
    productRates: "[data-product-shipping-rates]",
    productModal: "[data-product-shipping-modal]",
    productModalTrigger: "[data-product-shipping-modal-trigger]",
    productModalContent: "[data-product-shipping-modal-content]",
    productModalClose: "[data-product-shipping-modal-close]",
  };

  const escapeHtml = (value) =>
    String(value == null ? "" : value).replace(/[&<>"']/g, (char) => {
      const entities = {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      };
      return entities[char];
    });

  const deliveryMonthDayFormatter = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  });
  const deliveryMonthFormatter = new Intl.DateTimeFormat("en-US", { month: "short" });
  const deliveryDayFormatter = new Intl.DateTimeFormat("en-US", { day: "numeric" });

  const formatArrivesRange = (start, end) => {
    const sameMonth = start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth();
    const range = sameMonth
      ? `${deliveryMonthFormatter.format(start)} ${deliveryDayFormatter.format(start)}–${deliveryDayFormatter.format(end)}`
      : `${deliveryMonthDayFormatter.format(start)}–${deliveryMonthDayFormatter.format(end)}`;
    return `Arrives ${range}`;
  };

  const normalizeDeliveryPhrase = (delivery) => {
    const cleanDelivery = String(delivery || "").trim();
    if (!cleanDelivery) return "";

    const withoutPrefix = cleanDelivery.replace(/^arrives\s+/i, "");
    const rangeMatch = withoutPrefix.match(/^([A-Za-z]{3,})\s+(\d{1,2})\s*(?:-|–)\s*([A-Za-z]{3,})?\s*(\d{1,2})$/);
    if (rangeMatch) {
      const startMonth = rangeMatch[1];
      const startDay = rangeMatch[2];
      const endMonth = rangeMatch[3] || startMonth;
      const endDay = rangeMatch[4];
      const range =
        startMonth.toLowerCase() === endMonth.toLowerCase()
          ? `${startMonth} ${startDay}–${endDay}`
          : `${startMonth} ${startDay}–${endMonth} ${endDay}`;
      return `Arrives ${range}`;
    }

    return /^arrives\s+/i.test(cleanDelivery) ? cleanDelivery : `Arrives ${cleanDelivery.replace(/\s+-\s+/g, "–")}`;
  };

  class AbcShippingEstimator {
    constructor(root) {
      this.root = root;
      this.countrySelect = root.querySelector(selectors.country);
      this.status = root.querySelector(selectors.status);
      this.rates = root.querySelector(selectors.rates);
    }

    init() {
      if (!this.countrySelect || !this.status || !this.rates) return;
      this.countrySelect.addEventListener("change", () => this.render());
      this.countrySelect.addEventListener("input", () => this.render());
      this.render();
    }

    readRate() {
      const selectedOption = this.countrySelect.options[this.countrySelect.selectedIndex];
      if (!selectedOption) return null;

      const title = selectedOption.dataset.rateTitle || "";
      const description = selectedOption.dataset.rateDescription || "";
      const price = selectedOption.dataset.ratePrice || "";
      const delivery = selectedOption.dataset.rateDelivery || "";

      if (!title && !description && !price) return null;

      return {
        handle: selectedOption.value || "manual",
        title: this.normalizeTitle(title),
        freeText: this.normalizeFreeText(description),
        price: this.normalizePrice(price),
        delivery: normalizeDeliveryPhrase(delivery || this.formatDeliveryWindowForCountry(selectedOption.value)),
      };
    }

    render() {
      const rate = this.readRate();

      if (!rate) {
        this.renderStatus(this.root.dataset.emptyText || "Shipping options are unavailable for this selection.");
        return;
      }

      this.status.hidden = true;
      this.status.textContent = "";
      this.rates.hidden = false;
      this.rates.innerHTML = `
        <li class="abc-shipping-estimator__rate abc-shipping-estimator__rate--standard" data-rate-handle="${escapeHtml(rate.handle)}">
          <span class="abc-shipping-estimator__rate-title">${escapeHtml(rate.title)}:</span>
          ${rate.price ? `<strong class="abc-shipping-estimator__rate-price">${escapeHtml(rate.price)}</strong>` : ""}
          ${rate.freeText ? `<span class="abc-shipping-estimator__rate-note">${this.formatFreeHtml(rate.freeText)}</span>` : ""}
        </li>
        <li class="abc-shipping-estimator__rate abc-shipping-estimator__rate--quickship">
          <span class="abc-shipping-estimator__delivery-label">Estimated delivery:</span>
          <strong class="abc-shipping-estimator__delivery-date">${escapeHtml(rate.delivery)}</strong>
        </li>
      `;
    }

    normalizeTitle(title) {
      return (title || "Standard Shipping").trim();
    }

    normalizePrice(price) {
      const cleanPrice = (price || "").trim();
      if (!cleanPrice) return "";
      if (/^\$0\.00-\$9\.99$/i.test(cleanPrice) || /\$6\.99/i.test(cleanPrice)) return "US$6.99";
      return cleanPrice.replace(/^\$/, "US$");
    }

    normalizeFreeText(description) {
      const cleanDescription = (description || "").trim();
      if (!cleanDescription) return "";
      if (/free on orders/i.test(cleanDescription)) {
        return cleanDescription.replace(/\$69(?:\.00)?(?:\s+and\s+up)?/i, "US$69+");
      }

      const freeMatch = cleanDescription.match(/Free for orders\s+\$?([0-9]+)(?:\.00)?\s+and\s+up/i);
      if (freeMatch) return `free on orders US$${freeMatch[1]}+`;

      return cleanDescription.split(" - ")[0];
    }

    formatFreeHtml(freeText) {
      const escapedText = escapeHtml(freeText);
      return escapedText.replace(/(US\$[0-9]+[+]?)/i, "<strong>$1</strong>");
    }

    formatDeliveryWindow(startOffsetDays, endOffsetDays) {
      const start = new Date();
      const end = new Date();
      start.setDate(start.getDate() + startOffsetDays);
      end.setDate(end.getDate() + endOffsetDays);

      return formatArrivesRange(start, end);
    }

    formatDeliveryWindowForCountry(countryCode) {
      const normalizedCountryCode = String(countryCode || "").toUpperCase();
      const windows = {
        AU: [12, 15],
        CA: [12, 15],
        GB: [7, 11],
        US: [9, 16],
      };
      const windowRange = windows[normalizedCountryCode] || [9, 16];
      return this.formatDeliveryWindow(windowRange[0], windowRange[1]);
    }

    renderStatus(message) {
      this.rates.hidden = true;
      this.rates.innerHTML = "";
      this.status.hidden = false;
      this.status.textContent = message;
    }
  }

  class AbcProductShippingCountry {
    constructor(root) {
      this.root = root;
      this.countrySelect = root.querySelector(selectors.productCountry);
      this.status = root.querySelector(selectors.productStatus);
      this.rates = root.querySelector(selectors.productRates);
      this.modal = root.querySelector(selectors.productModal);
      this.modalPanel = this.modal?.querySelector(".abc-product-popup__panel");
      this.modalContent = this.modal?.querySelector(selectors.productModalContent);
      this.modalTriggers = root.querySelectorAll(selectors.productModalTrigger);
      this.modalCloseButtons = root.querySelectorAll(selectors.productModalClose);
      this.customCountry = null;
      this.customCountryButton = null;
      this.customCountryText = null;
      this.customCountryList = null;
      this.modalContentLoaded = false;
      this.modalContentLoading = false;
      this.methods = [];
      this.countries = [];
      this.previousFocus = null;
      this.handleEscape = (event) => {
        if (event.key === "Escape") this.closeModal();
      };
    }

    init() {
      if (!this.countrySelect || !this.status || !this.rates) return;
      this.applyCompactCountrySelect();
      this.buildCountryMethods();
      this.rebuildCountrySelect();
      this.buildCustomCountrySelect();
      this.countrySelect.addEventListener("change", () => this.render());
      this.countrySelect.addEventListener("input", () => this.render());
      document.addEventListener("click", (event) => {
        if (this.customCountry && !this.customCountry.contains(event.target)) this.closeCustomCountrySelect();
      });
      this.modalTriggers.forEach((trigger) => {
        trigger.addEventListener("click", (event) => {
          if (!this.modal) return;
          event.preventDefault();
          this.openModal();
        });
      });
      this.modalCloseButtons.forEach((button) => {
        button.addEventListener("click", () => this.closeModal());
      });
      this.render();
      this.scheduleModalPagePreload();
    }

    applyCompactCountrySelect() {
      this.countrySelect.style.flex = "0 1 170px";
      this.countrySelect.style.width = "clamp(128px, 32vw, 170px)";
      this.countrySelect.style.maxWidth = "42vw";
      this.countrySelect.style.overflow = "hidden";
      this.countrySelect.style.textOverflow = "ellipsis";
      this.countrySelect.style.whiteSpace = "nowrap";
    }

    buildCountryMethods() {
      const countriesByCode = new Map();
      this.methods = Array.from(this.countrySelect.options)
        .map((option) => {
          const countryCode = String(option.value || "").trim().toUpperCase();
          const countryLabel = option.textContent.trim();
          if (!countryCode || !countryLabel) return null;

          if (!countriesByCode.has(countryCode)) {
            countriesByCode.set(countryCode, {
              code: countryCode,
              label: countryLabel,
              selected: option.selected,
            });
          } else if (option.selected) {
            countriesByCode.get(countryCode).selected = true;
          }

          const deliveryStart = Number.parseInt(option.dataset.deliveryStart || "", 10);
          const deliveryEnd = Number.parseInt(option.dataset.deliveryEnd || "", 10);

          return {
            countryCode,
            countryLabel,
            title: this.normalizeTitle(option.dataset.rateTitle || "Standard Shipping"),
            price: this.normalizePrice(option.dataset.ratePrice || ""),
            freeText: this.normalizeFreeText(option.dataset.rateDescription || ""),
            delivery: this.formatDeliveryWindowForOption(countryCode, deliveryStart, deliveryEnd),
          };
        })
        .filter(Boolean);

      this.countries = Array.from(countriesByCode.values());
    }

    rebuildCountrySelect() {
      if (!this.countries.length) return;

      const selectedCountry = this.countries.find((country) => country.selected) || this.countries[0];
      this.countrySelect.innerHTML = this.countries
        .map(
          (country) =>
            `<option value="${escapeHtml(country.code)}"${country.code === selectedCountry.code ? " selected" : ""}>${escapeHtml(country.label)}</option>`
        )
        .join("");
    }

    buildCustomCountrySelect() {
      if (!this.countries.length || this.customCountry) return;
      const custom = document.createElement("div");
      custom.className = "abc-product-shipping-country__custom";
      custom.innerHTML = `
        <button type="button" class="abc-product-shipping-country__custom-trigger" aria-haspopup="listbox" aria-expanded="false">
          <span class="abc-product-shipping-country__custom-text" data-product-shipping-country-custom-text></span>
        </button>
        <div class="abc-product-shipping-country__custom-dropdown" hidden>
          <ul class="abc-product-shipping-country__custom-list" role="listbox"></ul>
        </div>
      `;
      this.countrySelect.insertAdjacentElement("afterend", custom);
      this.countrySelect.tabIndex = -1;
      this.countrySelect.setAttribute("aria-hidden", "true");
      this.root.classList.add("has-custom-country-select");
      this.customCountry = custom;
      this.customCountryButton = custom.querySelector(".abc-product-shipping-country__custom-trigger");
      this.customCountryText = custom.querySelector("[data-product-shipping-country-custom-text]");
      this.customCountryList = custom.querySelector(".abc-product-shipping-country__custom-list");
      this.customCountryButton?.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggleCustomCountrySelect();
      });
      this.renderCustomCountryOptions();
    }

    renderCustomCountryOptions() {
      if (!this.customCountryList) return;
      const selectedCountryCode = String(this.countrySelect.value || "").trim().toUpperCase();
      this.customCountryList.innerHTML = this.countries
        .map((country) => {
          const isSelected = country.code === selectedCountryCode;
          return `
            <li
              class="abc-product-shipping-country__custom-option${isSelected ? " is-active" : ""}"
              role="option"
              aria-selected="${isSelected ? "true" : "false"}"
              data-country-value="${escapeHtml(country.code)}"
            >
              <span>${escapeHtml(country.label)}</span>
            </li>
          `;
        })
        .join("");
      this.customCountryList.querySelectorAll("[data-country-value]").forEach((option) => {
        option.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.selectCustomCountry(option.dataset.countryValue || "");
        });
      });
    }

    selectCustomCountry(countryCode) {
      if (!countryCode || this.countrySelect.value === countryCode) {
        this.closeCustomCountrySelect();
        return;
      }
      this.countrySelect.value = countryCode;
      this.countrySelect.dispatchEvent(new Event("change", { bubbles: true }));
      this.closeCustomCountrySelect();
    }

    syncCustomCountrySelect() {
      if (!this.customCountryText) return;
      const selectedOption = this.countrySelect.options[this.countrySelect.selectedIndex];
      this.customCountryText.textContent = selectedOption ? selectedOption.textContent.trim() : "";
      this.renderCustomCountryOptions();
    }

    toggleCustomCountrySelect() {
      if (this.customCountry?.classList.contains("is-open")) {
        this.closeCustomCountrySelect();
      } else {
        this.openCustomCountrySelect();
      }
    }

    openCustomCountrySelect() {
      const dropdown = this.customCountry?.querySelector(".abc-product-shipping-country__custom-dropdown");
      if (!this.customCountry || !this.customCountryButton || !dropdown) return;
      dropdown.hidden = false;
      this.customCountry.classList.add("is-open");
      this.customCountryButton.setAttribute("aria-expanded", "true");
    }

    closeCustomCountrySelect() {
      const dropdown = this.customCountry?.querySelector(".abc-product-shipping-country__custom-dropdown");
      if (!this.customCountry || !this.customCountryButton || !dropdown) return;
      this.customCountry.classList.remove("is-open");
      this.customCountryButton.setAttribute("aria-expanded", "false");
      dropdown.hidden = true;
    }

    readRates() {
      const selectedCountryCode = String(this.countrySelect.value || "").trim().toUpperCase();
      return this.methods.filter((method) => method.countryCode === selectedCountryCode);
    }

    render() {
      this.syncCustomCountrySelect();
      const rates = this.readRates();

      if (!rates.length) {
        this.rates.hidden = true;
        this.rates.innerHTML = "";
        this.status.hidden = false;
        this.status.textContent = this.root.dataset.emptyText || "Shipping options are unavailable for this selection.";
        return;
      }

      this.status.hidden = true;
      this.status.textContent = "";
      this.rates.hidden = false;
      this.rates.innerHTML = rates
        .map((rate) => {
          const deliveryWindow = rate.delivery
            ? `<span class="abc-product-shipping-country__delivery abc-product-shipping-country__delivery--inline">(<strong class="abc-product-shipping-country__delivery-date">${escapeHtml(normalizeDeliveryPhrase(rate.delivery))}</strong>)</span>`
            : "";

          return `
        <li class="abc-product-shipping-country__rate abc-product-shipping-country__rate--method">
          <span class="abc-product-shipping-country__rate-main">
            <span class="abc-product-shipping-country__rate-title">${escapeHtml(rate.title)}:</span>
            ${rate.price ? `<strong class="abc-product-shipping-country__rate-price">${escapeHtml(rate.price)}</strong>` : ""}
            ${deliveryWindow}
          </span>
          ${rate.freeText ? `<span class="abc-product-shipping-country__rate-note">${this.formatFreeHtml(rate.freeText)}</span>` : ""}
        </li>
      `;
        })
        .join("");
    }

    openModal() {
      if (!this.modal) return;
      this.previousFocus = document.activeElement;
      if (this.modal.parentElement !== document.body) document.body.appendChild(this.modal);
      this.modal.hidden = false;
      this.loadModalPageContent();
      document.documentElement.classList.add("abc-product-popup-open", "abc-product-shipping-modal-open", "prevent-scroll");
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          this.modal.classList.add("is-open");
          this.modalPanel?.focus();
        });
      });
      document.addEventListener("keydown", this.handleEscape);
    }

    scheduleModalPagePreload() {
      if (!this.modal || !this.modalContent || !this.modal.dataset.shippingDetailsUrl) return;

      const preload = () => {
        if (this.modalContentLoaded || this.modalContentLoading) return;
        const run = () => this.loadModalPageContent();
        if ("requestIdleCallback" in window) {
          window.requestIdleCallback(run, { timeout: 2000 });
        } else {
          window.setTimeout(run, 800);
        }
      };

      if (document.readyState === "complete") {
        preload();
      } else {
        window.addEventListener("load", preload, { once: true });
      }
    }

    loadModalPageContent() {
      if (!this.modal || !this.modalContent || this.modalContentLoaded || this.modalContentLoading) return;
      const sourceUrl = this.modal.dataset.shippingDetailsUrl;
      if (!sourceUrl) return;

      this.modalContentLoading = true;
      this.modalContent.classList.add("is-loading");
      fetch(sourceUrl, { credentials: "same-origin" })
        .then((response) => {
          if (!response.ok) throw new Error(`Shipping details page failed: ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const pageContent = this.extractModalPageContent(html);
          if (!pageContent) return;
          this.modalContent.innerHTML = "";
          this.modalContent.append(...Array.from(pageContent.childNodes));
          this.modalContent.classList.add("abc-product-shipping-country__modal-content--remote");
          this.activateModalContentScripts();
          this.modalContentLoaded = true;
        })
        .catch(() => {
          this.modalContentLoaded = false;
        })
        .finally(() => {
          this.modalContentLoading = false;
          this.modalContent.classList.remove("is-loading");
        });
    }

    extractModalPageContent(html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const mainContent = doc.querySelector("#MainContent") || doc.querySelector("main[role='main']") || doc.querySelector("main");
      if (!mainContent) return null;

      const content = document.createElement("div");
      Array.from(mainContent.childNodes).forEach((node) => {
        content.appendChild(document.importNode(node, true));
      });

      const modalTitle = this.modal?.querySelector(".abc-product-shipping-country__modal-title")?.textContent.trim().toLowerCase();
      const firstElement = Array.from(content.children).find((child) => child.textContent.trim());
      if (modalTitle && firstElement && firstElement.textContent.trim().toLowerCase() === modalTitle) {
        firstElement.remove();
      }

      return content.textContent.trim() ? content : null;
    }

    activateModalContentScripts() {
      if (!this.modalContent) return;

      this.modalContent.querySelectorAll("script").forEach((script) => {
        const type = (script.getAttribute("type") || "").trim().toLowerCase();
        const isJavaScript =
          !type ||
          type === "module" ||
          type === "text/javascript" ||
          type === "application/javascript" ||
          type === "application/ecmascript" ||
          type === "text/ecmascript";

        if (!isJavaScript) return;

        const executable = document.createElement("script");
        Array.from(script.attributes).forEach((attribute) => {
          executable.setAttribute(attribute.name, attribute.value);
        });
        executable.textContent = script.textContent;
        script.replaceWith(executable);
      });
    }

    closeModal() {
      if (!this.modal || this.modal.hidden) return;
      this.modal.classList.remove("is-open");
      document.documentElement.classList.remove("abc-product-popup-open", "abc-product-shipping-modal-open", "prevent-scroll");
      document.removeEventListener("keydown", this.handleEscape);
      window.setTimeout(() => {
        this.modal.hidden = true;
        if (this.previousFocus && typeof this.previousFocus.focus === "function") this.previousFocus.focus();
      }, 300);
    }

    normalizeTitle(title) {
      return (title || "Standard Shipping").trim();
    }

    normalizePrice(price) {
      const cleanPrice = (price || "").trim();
      if (!cleanPrice) return "";
      return cleanPrice.replace(/^\$/, "US$");
    }

    normalizeFreeText(description) {
      const cleanDescription = (description || "").trim();
      if (!cleanDescription) return "";
      return cleanDescription.replace(/\$69(?:\.00)?(?:\s+and\s+up)?/i, "US$69+");
    }

    formatFreeHtml(freeText) {
      const escapedText = escapeHtml(freeText);
      return escapedText.replace(/(US\$[0-9]+[+]?)/i, "<strong>$1</strong>");
    }

    formatDeliveryWindowForOption(countryCode, deliveryStart, deliveryEnd) {
      if (Number.isFinite(deliveryStart) && Number.isFinite(deliveryEnd)) {
        return this.formatDeliveryWindow(deliveryStart, deliveryEnd);
      }

      const normalizedCountryCode = String(countryCode || "").toUpperCase();
      const windows = {
        AU: [12, 15],
        CA: [12, 15],
        GB: [7, 11],
        US: [9, 16],
      };
      const windowRange = windows[normalizedCountryCode] || [9, 16];
      return this.formatDeliveryWindow(windowRange[0], windowRange[1]);
    }

    formatDeliveryWindow(startOffsetDays, endOffsetDays) {
      const start = new Date();
      const end = new Date();
      start.setDate(start.getDate() + startOffsetDays);
      end.setDate(end.getDate() + endOffsetDays);

      return formatArrivesRange(start, end);
    }
  }

  const init = (context = document) => {
    context.querySelectorAll(selectors.root).forEach((root) => {
      if (root.dataset.shippingEstimatorReady === "true") return;
      root.dataset.shippingEstimatorReady = "true";
      new AbcShippingEstimator(root).init();
    });

    context.querySelectorAll(selectors.productRoot).forEach((root) => {
      if (root.dataset.productShippingCountryReady === "true") return;
      root.dataset.productShippingCountryReady = "true";
      new AbcProductShippingCountry(root).init();
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init());
  } else {
    init();
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener("shopify:section:load", (event) => init(event.target));
  }
})();
